import React, { useContext, useMemo, useState } from 'react';
import { Users } from '../../../models/User.tsx';
import { Timestamp } from 'firebase/firestore';
import { messaging } from '../../../services/admin/Messaging.tsx';
import { useUser } from '../../../context/UserContext.tsx';
import AgentContext from '../../../context/admin/Agent.tsx';
import {
    BULK_RECIPIENT_LABELS,
    filterMessagingUsers,
    getMessagingRecipientsByType,
} from '../../../helpers/messagingRecipients.ts';
import RecipientsPanel from './RecipientsPanel.tsx';
import '../../../styles/Messaging.css';

const ComposeMessaging = () => {

    const { user: currentUser } = useUser();
    const { allAgents, allPlayers, loading: agentSummaryLoading } = useContext(AgentContext);
    const [searchResults, setSearchResults] = useState<Users[]>([]);
    const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
    const [formData, setFormData] = useState({ recipientUsers: '', subject: '', messageBody: '' });
    const [selectedOption, setSelectedOption] = useState('');
    const [showSuccessMessage, setShowSuccessMessage] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [isSending, setIsSending] = useState(false);

    const searchableUsers = useMemo(
        () => [...allAgents, ...allPlayers],
        [allAgents, allPlayers]
    );

    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = event.target;
        setFormData(prevFormData => ({
            ...prevFormData,
            [name]: value,
        }));

        if (name === 'recipientUsers') {
            if (value.trim() !== '') {
                setSearchResults(filterMessagingUsers(searchableUsers, value.trim()));
            } else {
                setSearchResults([]);
            }
        }
    };

    const handleResultSelect = (userName: string) => {
        if (!selectedUsers.includes(userName)) {
            setSelectedUsers(prev => [...prev, userName]);
        }
        setSearchResults([]);
        setFormData(prev => ({ ...prev, recipientUsers: '' }));
    };

    const handleSelectChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const { value } = event.target;
        setSelectedOption(value);
        const users = getMessagingRecipientsByType(value, allAgents, allPlayers);
        setSelectedUsers(users.map(user => user.userName));
    };

    const save = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!currentUser?.userName) return;

        if (selectedUsers.length === 0) {
            setErrorMessage('Please select at least one recipient.');
            return;
        }
        if (!formData.subject.trim() || !formData.messageBody.trim()) {
            setErrorMessage('All fields are required.');
            return;
        }

        setIsSending(true);
        const newMessaging = {
            senderUser: currentUser.userName,
            recipientUsers: selectedUsers,
            messageBody: formData.messageBody.trim(),
            subject: formData.subject.trim(),
            createDate: Timestamp.now(),
            ...(selectedOption && BULK_RECIPIENT_LABELS[selectedOption]
                ? { recipientLabel: BULK_RECIPIENT_LABELS[selectedOption] }
                : {}),
        };

        try {
            await messaging(newMessaging);
            setFormData({ recipientUsers: '', subject: '', messageBody: '' });
            setSelectedUsers([]);
            setSelectedOption('');
            setShowSuccessMessage(true);
            setErrorMessage('');
            setTimeout(() => setShowSuccessMessage(false), 3000);
        } catch (error) {
            setErrorMessage('Error sending message. Please try again.');
        } finally {
            setIsSending(false);
        }
    };

    const handleRemoveUser = (userName: string) => {
        setSelectedUsers(prev => prev.filter(user => user !== userName));
        setSelectedOption('');
    };

    const handleClearRecipients = () => {
        setSelectedUsers([]);
        setSelectedOption('');
    };

    const groupLabel = selectedOption ? BULK_RECIPIENT_LABELS[selectedOption] : undefined;

    const renderSelectOptions = () => {
        if (currentUser?.userType === 'masterAgent') {
            return (
                <>
                    <option value="">Specific Account(s)</option>
                    <option value="allPlayer">All Players</option>
                    <option value="allAgent">All Agents</option>
                    <option value="all">All Players and Agents</option>
                </>
            );
        }
        if (currentUser?.userType === 'subAgent') {
            return (
                <>
                    <option value="">Specific Account(s)</option>
                    <option value="allPlayer">All Players</option>
                </>
            );
        }
        return null;
    };

    if (!currentUser) return <div>Loading...</div>;

    return (
        <div className='mt-2 container-messag'>
            <div className="inbox-header">
                <span>COMPOSE MESSAGE</span>
            </div>
            <div className="line-block-messag"></div>
            <form className="message-area" onSubmit={save}>
                <div className="compose-form-group">
                    <label className="compose-field-label">Recipients</label>
                    <div className="compose-recipients-toolbar">
                        {selectedOption === '' && (
                            <div className="compose-search-wrapper">
                                <input
                                    id="recipient"
                                    name="recipientUsers"
                                    type="text"
                                    className="search-users"
                                    placeholder="Search users..."
                                    value={formData.recipientUsers}
                                    onChange={handleInputChange}
                                    autoComplete="off"
                                    disabled={agentSummaryLoading}
                                />
                                {searchResults.length > 0 && (
                                    <div className="messaging-search-container">
                                        {searchResults.map((user) => (
                                            <div
                                                key={user.id ?? user.userName}
                                                className="messaging-search-item"
                                                onClick={() => handleResultSelect(user.userName)}
                                            >
                                                {user.userName}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}
                        <select
                            id="select-user"
                            name="select-user"
                            className="select-messegen"
                            value={selectedOption}
                            onChange={handleSelectChange}
                            disabled={agentSummaryLoading}
                        >
                            {renderSelectOptions()}
                        </select>
                    </div>
                    {agentSummaryLoading && (
                        <span className="selected-users-count">Loading accounts...</span>
                    )}
                    {selectedUsers.length > 0 && (
                        <RecipientsPanel
                            recipients={selectedUsers}
                            groupLabel={groupLabel}
                            onRemove={handleRemoveUser}
                            onClearAll={handleClearRecipients}
                        />
                    )}
                </div>
                <div className="compose-form-group">
                    <label htmlFor="subject" className="compose-field-label">Subject</label>
                    <input
                        id="subject"
                        name="subject"
                        type="text"
                        className="subject-users"
                        value={formData.subject}
                        onChange={handleInputChange}
                        placeholder="Enter subject..."
                    />
                </div>
                <div className="compose-form-group">
                    <label htmlFor="messageBody" className="compose-field-label">Message</label>
                    <textarea
                        id="messageBody"
                        name="messageBody"
                        className='container-text'
                        value={formData.messageBody}
                        onChange={handleInputChange}
                        placeholder="Type your message..."
                    />
                </div>
                <button
                    type="submit"
                    className="btn btn-primary button-messagin"
                    disabled={isSending || selectedUsers.length === 0 || agentSummaryLoading}
                >
                    {isSending ? 'Sending...' : 'Send'}
                </button>
                {showSuccessMessage && (
                    <div className="success-message">Message sent successfully.</div>
                )}
                {errorMessage && (
                    <div className="error-message">{errorMessage}</div>
                )}
            </form>
        </div>
    );
};

export default ComposeMessaging;
